import FlexContainer from '../../../common/FlexContainer';
import {FormResults} from '../../../common/Form';
import Spinner from '../../../common/loading/Spinner';
import Typography from '../../../common/text/Typography';
import {useOperateAttrMutation} from '../../api';
import {mergeErrorStates, extractQueryErrMessage} from '../../api/helpers';
import {BaseAttribute} from '../../ontology/types/attributeTypes';
import {
    renderReferenceField,
    renderDerivationSubfields,
} from '../common/jsxHelpers';
import useAttrNeighbors from '../hooks/useAttrNeighbors';
import {
    addAttrFormDefaults,
    addAttrFormToPayload,
} from './addAttributeHelpers';
import {AddAttrFormValues} from './addAttributeTypes';
import React, {FunctionComponent, useCallback, useEffect} from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import AppModal from "../../../common/modals/AppModal";
import ValueTypeSelect from "../../../common/inputs/ValueTypeSelect";
import IsDerivationInput from "../../../common/inputs/IsDerivationInput";
import MissingValuesInput from "../../../common/inputs/MissingValuesInput";
import useModalType from "../../HUD/hooks/useModalType";
import Uninitialized from "../../../common/loading/Uninitialized";
import Loading from "../../../common/loading/Loading";

const AddAttributeForm: FunctionComponent = () => {

    const {modalProps, closeModal} = useModalType();
	const {objectId: _id, canEdit} = modalProps;

    const formDefaults = addAttrFormDefaults();

    const {handleSubmit, register, resetField, reset, formState, watch} =
        useForm<AddAttrFormValues>({
            defaultValues: formDefaults,
        });

    const [watchedDerivationFlag, watchedUsingReferenceValue] = watch([
        'isDerivation',
        'usingStaticReference',
    ]);

    // prevent filtering from running every render
    const filter = useCallback(
        (attr: BaseAttribute) => attr.type === 'quantity',
        []
    );

    const validatingRegister = useCallback(
        (inputName: keyof AddAttrFormValues, options?: any) =>
            register(inputName,  options ? options : {
                required: `${inputName} is required`,
            }),
        [register]
    );

    const queryRes = useAttrNeighbors(_id, filter);

    // useEffect(() => {
    //     if (queryRes.isSuccess && closeModal) {
    //         closeModal();
    //     }
    // }, [queryRes.isSuccess, closeModal]);

    const [addAttr, mutationResults] = useOperateAttrMutation();

    const mergedErrs = mergeErrorStates(queryRes, mutationResults);

    const onSubmit: SubmitHandler<AddAttrFormValues> = (vals, e) => {
        e?.preventDefault();
        addAttr({
            attrId: _id,
            body: addAttrFormToPayload(vals),
        });
    };

    if (queryRes.isError) {
        return (
            <FlexContainer justifyContent="center">
                <Typography color="error" paragraph>
                    {extractQueryErrMessage(queryRes.error)}
                </Typography>
            </FlexContainer>
        );
    }

    return (
        <AppModal
            label={"Add Attribute"}
            isOpen={true}
            isDirty={formState.isDirty}
            onClose={closeModal}
            onSubmit={handleSubmit(onSubmit)}
            canEdit={canEdit ? canEdit : true}>
            <IsDerivationInput
                resetField={resetField}
                formState={formState}
                validatingRegister={validatingRegister}
                mutationResults={mutationResults}
            />
            {renderDerivationSubfields({
                isDerivation: watchedDerivationFlag === 'true',
                formState,
                validatingRegister,
                resetField,
                mutationResults
            })}
            <ValueTypeSelect
                resetField={resetField}
                formState={formState}
                validatingRegister={validatingRegister}
                mutationResults={mutationResults}
            />
            {!queryRes.isUninitialized && !queryRes.isLoading ?
                renderReferenceField(
                'otherId',
                'otherValue',
                watchedUsingReferenceValue === 'true',
                validatingRegister,
                queryRes.attrNeighbors,
                formState
            ) : queryRes.isUninitialized ? <Uninitialized /> : <Loading />}
            <MissingValuesInput
                resetField={resetField}
                formState={formState}
                validatingRegister={validatingRegister}
                mutationResults={mutationResults}
            />
            <FormResults
                isError={!!mergedErrs}
                error={mergedErrs}
                validationErrors={formState.errors}
            />
        </AppModal>
    );
};

export default AddAttributeForm;
